import { Suspense, useEffect, useState } from "react"
import { Navigate, Outlet, useLocation } from "react-router-dom"
import { CommandPalette } from "../components/CommandPalette"
import { Sidebar } from "../components/Sidebar"
import { TopBar } from "../components/TopBar"
import { PageLoader } from "../components/ui/page-loader"
import { Sheet, SheetContent } from "../components/ui/sheet"
import { useAuth } from "../contexts/AuthContext"
import { useIsDesktop } from "../hooks/useMediaQuery"
import {
  adminNavigationConfig,
  canAccessAdminSection,
  filterAdminNavForRole,
  resolveAdminSection,
} from "../libs/admin-navigation"

export function AdminLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const isDesktop = useIsDesktop()
  const { user } = useAuth()
  const location = useLocation()

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  // Nav is filtered to the sections this platform role may access. All hooks
  // above run unconditionally before the per-section guard's early return.
  const navConfig = filterAdminNavForRole(adminNavigationConfig, user?.platformRole)

  // Per-section access guard. Overview is open to platform users (global_admin
  // and global_ops), but if a role can't access it, send it to the first
  // available section instead of an Access Denied. The backend independently
  // enforces these gates.
  const section = resolveAdminSection(location.pathname)
  const firstSection = navConfig.find((item) => item.path !== "/admin")?.path
  if (section === "overview" && !canAccessAdminSection(user?.platformRole, "overview")) {
    return <Navigate to={firstSection ?? "/"} replace />
  }
  const sectionDenied = section != null && !canAccessAdminSection(user?.platformRole, section)

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* Desktop Sidebar */}
      {isDesktop && <Sidebar navigationConfig={navConfig} className="hidden desktop:flex" />}

      {/* Mobile Drawer */}
      {!isDesktop && (
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetContent side="left" className="w-sidebar p-0">
            <Sidebar navigationConfig={navConfig} onCollapse={() => setMobileMenuOpen(false)} />
          </SheetContent>
        </Sheet>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <TopBar
          onMenuClick={() => setMobileMenuOpen(true)}
          onCommandPalette={() => setPaletteOpen(true)}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-bg" aria-label="Admin content">
          {sectionDenied ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <h1 className="mb-2 font-display text-2xl font-bold text-content">Access Denied</h1>
                <p className="text-content-2">You do not have access to this section.</p>
              </div>
            </div>
          ) : (
            // Local Suspense so lazy admin page swaps keep the shell visible.
            <Suspense fallback={<PageLoader className="h-full" />}>
              <Outlet />
            </Suspense>
          )}
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  )
}
